import {
  ChangeDetectionStrategy,
  Component,
  SimpleChanges
} from '@angular/core';
import { ColDef, IServerSideGetRowsParams } from 'ag-grid-enterprise';
import { Observable, defer, forkJoin, of } from 'rxjs';
import { catchError, shareReplay } from 'rxjs/operators';
import { AuthService } from 'src/app/services/auth.service';
import { ESignService } from 'src/app/services/esign.service';
import { GridService } from 'src/app/services/grid.service';
import { UtilityService } from 'src/app/services/utility.service';
import { esignTemplatesService } from 'src/app/shared/vive-api/esign-templates.service';
import { EsignTemplatesUnmappedFieldReportResponseDto } from 'src/app/shared/vive-api/vive-api-models';
import { LOAD_BALANCER_TIMEOUT_MS } from 'src/app/shared/vive-api/vive-api.config';

import { BaseGridComponent } from '../base-grid/base-grid.component';

type ESignProviderOption = { id: number; name: string };

/**
 * How long one fetch of the unmapped-field report, or of the provider list,
 * keeps serving the grid.
 *
 * Neither changes with paging, sorting or filtering, so re-requesting them on
 * every block load bought nothing. For the report it was actively harmful:
 * it's a backend sweep across every VIVE template, and each block load
 * started another one. The bound exists for OTHER admins' changes. A save or
 * archive in this session already drops the report on reload (see
 * `ngOnChanges`), but a template someone else publishes only shows up once
 * this expires.
 */
const REPORT_CACHE_TTL_MS = 5 * 60 * 1000;

@Component({
  selector: 'app-esign-templates-grid',
  templateUrl: '../base-grid/base-grid.component.html',
  styleUrls: [
    '../base-grid/base-grid.component.less',
    './esign-templates-grid.component.less'
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class ESignTemplatesGridComponent extends BaseGridComponent {
  private unmappedFieldReport$: Observable<
    EsignTemplatesUnmappedFieldReportResponseDto[]
  > | null = null;
  private unmappedFieldReportFetchedAt = 0;
  private esignProviders$: Observable<ESignProviderOption[]> | null = null;
  private esignProvidersFetchedAt = 0;

  constructor(
    private ESignService: ESignService,
    protected override GridService: GridService,
    protected override UtilityService: UtilityService,
    protected override AuthService: AuthService
  ) {
    super(GridService, UtilityService, AuthService);

    this.gridPrimaryKeyFieldName = 'id';
    this.gridType = 'ESignTemplate';
    this.rowModelType = 'serverSide';

    this.ServerSideDataSource = {
      getRows: (params: IServerSideGetRowsParams) => {
        const { request } = params;

        const queryObject = this.constructGridQuery({ request });
        const countQueryObject = this.constructGridQuery(
          { request },
          { isCountQuery: true }
        );

        forkJoin([
          this.ESignService.getESignTemplates(queryObject),
          this.ESignService.getESignTemplateCount(countQueryObject),
          // One request per grid load rather than one per block: the report
          // covers every VIVE template whatever page, sort or filter is
          // showing, so every block load shares one fetch until a reload or
          // the TTL. See `cachedUnmappedFieldReport`.
          this.cachedUnmappedFieldReport(),
          // Which provider id is VIVE, resolved by NAME rather than assumed.
          // The row carries `ESignProviderId` and not the `ESignProvider`
          // association — the grid route's `attributes` is an allow-list and
          // does not pull associations at all — so the renderer cannot read a
          // provider name off the row. Resolving it here is what lets it tell
          // a VIVE template from a DocuSign one.
          this.cachedESignProviders()
        ]).subscribe({
          next: ([
            ESignTemplates,
            esignTemplateCount,
            unmappedFieldReport,
            esignProviders
          ]) => {
            const reportByTemplateId = new Map(
              unmappedFieldReport.map((entry) => [entry.ESignTemplateId, entry])
            );
            const viveProviderId = esignProviders.find(
              (provider) => provider.name === 'VIVE'
            )?.id;

            params.success({
              rowData: ESignTemplates.map(
                (ESignTemplate: { id: number; ESignProviderId?: number }) => ({
                  ...ESignTemplate,
                  unmappedFieldReport: reportByTemplateId.get(ESignTemplate.id),
                  // Undefined VIVE id means the provider list did not load, and
                  // this reads false — the column degrades to "not applicable"
                  // rather than claiming a DocuSign template is unmapped.
                  isViveTemplate:
                    viveProviderId !== undefined &&
                    ESignTemplate.ESignProviderId === viveProviderId
                })
              ),
              rowCount: esignTemplateCount
            });
          },
          error: () => {
            params.fail();
          }
        });
      }
    };
  }

  /**
   * A save or archive sets `reloadGridData`, and the base class answers with a
   * purge-refresh. The report is dropped first, so that refresh reads the
   * mapping the admin just changed and not the one they replaced.
   *
   * Only on the change TO true. The grid emits the change back to false once
   * the model updates, and the base class refreshes on that one too. Dropping
   * the report there as well would run a second sweep for every save.
   *
   * The provider list is kept: saving or archiving a template doesn't change
   * which providers exist.
   */
  public override ngOnChanges(changes: SimpleChanges): void {
    if (changes['reloadGridData']?.currentValue === true) {
      this.unmappedFieldReport$ = null;
    }

    super.ngOnChanges(changes);
  }

  /**
   * The unmapped-field report, fetched once and shared by every block load
   * until a reload or the TTL.
   *
   * `shareReplay` makes concurrent block loads share the one in-flight request
   * and replays the answer to later ones.
   *
   * A failure is cached too, as the empty report the column already degrades
   * to. The request it replaces is a sweep that has run up against the load
   * balancer's 60s timeout, and retrying it on every page would restart that
   * sweep once per click during exactly that outage. The handler never writes
   * the field, so a late error on a stream a reload already replaced can't
   * evict its replacement.
   */
  private cachedUnmappedFieldReport(): Observable<
    EsignTemplatesUnmappedFieldReportResponseDto[]
  > {
    if (
      !this.unmappedFieldReport$ ||
      this.hasExpired(this.unmappedFieldReportFetchedAt)
    ) {
      this.unmappedFieldReport$ = defer(() =>
        esignTemplatesService.reportUnmappedTemplateFields({
          options: { requestTimeout: LOAD_BALANCER_TIMEOUT_MS }
        })
      ).pipe(
        catchError(() =>
          of([] as EsignTemplatesUnmappedFieldReportResponseDto[])
        ),
        shareReplay({ bufferSize: 1, refCount: false })
      );
      this.unmappedFieldReportFetchedAt = Date.now();
    }

    return this.unmappedFieldReport$;
  }

  /**
   * The provider list, shared the same way, except that a failure is NOT kept.
   *
   * It's a cheap request with no sweep behind it, and holding its failure
   * would read every row as "not applicable", including a VIVE template that
   * genuinely has gaps, for the whole TTL. It's evicted instead, so the next
   * block load asks again. The identity check means a late error can only
   * evict the stream that failed, never one that has since replaced it.
   */
  private cachedESignProviders(): Observable<ESignProviderOption[]> {
    if (
      !this.esignProviders$ ||
      this.hasExpired(this.esignProvidersFetchedAt)
    ) {
      const esignProviders$: Observable<ESignProviderOption[]> =
        this.ESignService.getESignProviders().pipe(
          catchError(() => {
            if (this.esignProviders$ === esignProviders$) {
              this.esignProviders$ = null;
            }

            return of([] as ESignProviderOption[]);
          }),
          shareReplay({ bufferSize: 1, refCount: false })
        );

      this.esignProviders$ = esignProviders$;
      this.esignProvidersFetchedAt = Date.now();
    }

    return this.esignProviders$;
  }

  private hasExpired(fetchedAt: number): boolean {
    return Date.now() - fetchedAt >= REPORT_CACHE_TTL_MS;
  }

  /**
   * The backend serves `mappingFingerprint` hidden and unrendered on purpose —
   * it deploys before this app, and its raw value is an md5. Naming the renderer
   * here is what turns it into mapping health, and keeps the two deploys
   * independent.
   */
  protected override applyColumnOverrides(ColDefs: ColDef[]): ColDef[] {
    const mappingColumn = ColDefs.find(
      (colDef) => colDef.field === 'mappingFingerprint'
    );

    if (mappingColumn) {
      Object.assign(mappingColumn, {
        hide: false,
        cellRenderer: 'FieldMappingCellRenderer',
        // A fingerprint is an md5; sorting or filtering on it would offer the
        // admin an ordering that means nothing.
        sortable: false,
        filter: false
      });
    }

    return ColDefs;
  }
}
